// src/App.js
import "./print.css";
import React, { useEffect, useState } from "react";
import {
  Routes,
  Route,
  NavLink,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Billing from "./pages/Billing";
import ClientsJobs from "./components/ClientsJobs";
import Servers from "./components/Servers";
import JobDetail from "./pages/JobDetail";
import ServerEdit from "./pages/ServerEdit";
import ClientDetail from "./pages/ClientDetail";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import WorkspaceCreated from "./pages/WorkspaceCreated";
import BusinessProfile from "./pages/BusinessProfile";
import ClientManagement from "./pages/ClientManagement";
import Reports from "./pages/Reports";
import Books from "./pages/Books";
import WorkspaceDashboard from "./pages/WorkspaceDashboard";
import AppShell from "./components/layout/AppShell";
import { AuthAPI } from "./api";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ClientEdit from "./pages/ClientEdit";
import ClientNew from "./pages/ClientNew";
import Employees from "./pages/Employees";
import ProductsServices from "./pages/ProductsServices";
import ThumbtackIntegration from "./pages/ThumbtackIntegration";
import ThumbtackLeads from "./pages/ThumbtackLeads";

// Platform
import PlatformRoute from "./components/PlatformRoute";
import PlatformDashboard from "./pages/PlatformDashboard";
import PlatformTenantIntelligence from "./pages/PlatformTenantIntelligence";
import PlatformTenants from "./pages/PlatformTenants";
import PlatformTenantDetail from "./pages/PlatformTenantDetail";
import PlatformBillingControl from "./pages/platform/PlatformBillingControl";
import PlatformStripeBilling from "./pages/platform/PlatformStripeBilling";
import PlatformStripeConnect from "./pages/platform/PlatformStripeConnect";
import PlatformReleaseCenter from "./pages/platform/PlatformReleaseCenter";
import PlatformBetaControls from "./pages/platform/PlatformBetaControls";

// Billing
import BillingPage from "./billing/pages/BillingPage";
import BillingSuccess from "./billing/pages/BillingSuccess";
import BillingCancel from "./billing/pages/BillingCancel";
import BillingRequired from "./pages/BillingRequired";
import InvoicePaymentsReturn from "./pages/InvoicePaymentsReturn";
import Suspended from "./pages/Suspended";

// Invoices pages
import Invoices from "./pages/Invoices";
import InvoiceDetail from "./pages/InvoiceDetail";
import InvoicePrint from "./pages/InvoicePrint";
import InvoiceNew from "./pages/InvoiceNew";
import InvoiceReceipt from "./pages/InvoiceReceipt";

// Estimates pages
import EstimateDetail from "./pages/EstimateDetail";
import Estimates from "./pages/Estimates";
import EstimateNew from "./pages/EstimateNew";
import EstimatePrint from "./pages/EstimatePrint";

// Judgments pages
import JudgmentsPage from "./pages/JudgmentsPage";
import JudgmentDetail from "./pages/JudgmentDetail";
import CreateJudgmentWizard from "./pages/CreateJudgmentWizard";

const ProtectedRoute = ({ children, isAuthed, authChecked }) => {
  if (!authChecked) {
    return <div style={{ padding: 24, color: "white" }}>Loading...</div>;
  }

  if (!isAuthed) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const authPagePaths = [
    "/login",
    "/signup",
    "/beta-signup",
    "/workspace-created",
    "/forgot-password",
    "/reset-password",
  ];

  const hideGlobalNav = authPagePaths.some((path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`)
  );


  const [isAuthed, setIsAuthed] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [serverTime, setServerTime] = useState(null);
  const [isMobilePublicNav, setIsMobilePublicNav] = useState(false);
  const [publicMenuOpen, setPublicMenuOpen] = useState(false);
  const [me, setMe] = useState(null);

  const noAppShellPaths = [
    "/login",
    "/signup",
    "/workspace-created",
    "/forgot-password",
    "/reset-password",
    "/billing-required",
    "/suspended",
  ];

  const isStandaloneDocumentPage =
    location.pathname.includes("/print") ||
    location.pathname.includes("/receipt") ||
    location.pathname.includes("/pdf") ||
    location.pathname.includes("/preview");

  const usesAppShell =
    isAuthed &&
    !noAppShellPaths.includes(location.pathname) &&
    !isStandaloneDocumentPage;

  useEffect(() => {
    function handleMobilePublicNavResize() {
      const mobile = window.innerWidth <= 760;
      setIsMobilePublicNav(mobile);
      if (!mobile) setPublicMenuOpen(false);
    }

    handleMobilePublicNavResize();
    window.addEventListener("resize", handleMobilePublicNavResize);

    return () => window.removeEventListener("resize", handleMobilePublicNavResize);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const publicAuthSkipPaths = [
      "/login",
      "/signup",
      "/beta-signup",
      "/app/beta-signup",
      "/workspace-created",
      "/app/workspace-created",
      "/forgot-password",
      "/reset-password",
    ];

    if (publicAuthSkipPaths.includes(location.pathname)) {
      setIsAuthed(false);
      setAuthChecked(true);
      return () => {
        cancelled = true;
      };
    }

async function checkAuth() {
  try {
    
const data = await AuthAPI.me();

if (!cancelled) {
  setMe(data);

// SaaS entitlement routing comes from backend /auth/me access_status
const blockedAccessStatuses = [
  "trial_expired",
  "billing_required",
];

if (blockedAccessStatuses.includes(data.access_status)) {
  const billingRecoveryPaths = [
    "/billing-required",
    "/billing",
    "/billing/success",
    "/billing/cancel",
  ];

  setIsAuthed(true);
  setAuthChecked(true);

  if (!billingRecoveryPaths.includes(location.pathname)) {
    navigate("/billing-required", { replace: true });
  }

  return;
}

setIsAuthed(true);
setAuthChecked(true);
}

      } catch (err) { 
        if (!cancelled) {
          if (
            err?.status === 403 &&
            String(err?.body || err?.message || "").includes("Tenant suspended")
          ) {
            setIsAuthed(true);
            setAuthChecked(true);
            navigate("/suspended", { replace: true });
            return;
          }
          if (err?.status === 402) {
            setIsAuthed(false);
            setAuthChecked(true);
            navigate("/billing-required", { replace: true });
            return;
          }

          setIsAuthed(false);
          setAuthChecked(true);
        }
      }
    }

    checkAuth();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;

    async function loadTime() {
      if (!isAuthed) return;

      try {
        const res = await fetch("/api/time", { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setServerTime(data);
      } catch {
        // ignore
      }
    }

    loadTime();
    const id = setInterval(loadTime, 30000);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isAuthed]);

  const host = window.location.hostname;
  const isRootHost =
  host === "app.juliangonzales.com" ||
  host === "localhost" ||
  host === "127.0.0.1";
  const isTenantHost = host.endsWith(".app.juliangonzales.com") && !isRootHost;
  const tenantSubdomain = isTenantHost ? host.split(".")[0] : null;

  const linkStyle = ({ isActive }) => ({
    marginRight: 14,
    textDecoration: "none",
    color: isActive ? "#00c2ff" : "#ffffff",
    fontWeight: isActive ? 700 : 500,
  });

  async function handleLogout() {
    try {
      await AuthAPI.logout();
    } catch (e) {
      console.warn("logout failed silently", e);
    } finally {
      setIsAuthed(false);
      navigate("/login", { replace: true });
    }
  }

  if (!authChecked) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#0b1b2b",
          color: "white",
          padding: 24,
        }}
      >
        Loading...
      </div>
    );
  }

const billingAllowedPaths = [
  "/billing-required",
  "/billing",
  "/billing/success",
  "/billing/cancel",
];

const billingBlockedStatuses = [
  "trial_expired",
  "billing_required",
];

if (
  isAuthed &&
  billingBlockedStatuses.includes(me?.access_status) &&
  !billingAllowedPaths.includes(window.location.pathname)
) {
  return <Navigate to="/billing-required" replace />;
}

  return (
    <div
      className="app-root-canvas"
      style={{
        background: isStandaloneDocumentPage ? "#ffffff" : "#0b1b2b",
        minHeight: "100vh",
        color: isStandaloneDocumentPage ? "#12202f" : "white",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {!usesAppShell && !hideGlobalNav && <header
          style={{
            background: "#13293d",
            padding: isMobilePublicNav ? "12px 16px" : "16px 24px",
            display: "flex",
            alignItems: isMobilePublicNav ? "stretch" : "center",
            justifyContent: "space-between",
            gap: isMobilePublicNav ? 10 : 12,
            flexDirection: isMobilePublicNav ? "column" : "row",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: isMobilePublicNav ? "stretch" : "center",
              justifyContent: isMobilePublicNav ? "space-between" : "flex-start",
              gap: isMobilePublicNav ? 10 : 10,
              flexWrap: "wrap",
              width: isMobilePublicNav ? "100%" : "auto",
            }}
          >
            {isMobilePublicNav && (
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 12,
                    background: "#f4c542",
                    color: "#071827",
                    display: "grid",
                    placeItems: "center",
                    fontWeight: 900,
                    fontSize: 18,
                  }}
                >
                  JT
                </div>
                <div>
                  <div style={{ fontWeight: 900, fontSize: 20, lineHeight: 1.1 }}>
                    JobTelesis
                  </div>
                  <div style={{ color: "#9fb0c3", fontSize: 13 }}>
                    Operations software
                  </div>
                </div>
              </div>
            )}

            {isMobilePublicNav && (
              <button
                type="button"
                onClick={() => setPublicMenuOpen((v) => !v)}
                style={{
                  padding: "10px 14px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.14)",
                  background: "#00c2ff",
                  color: "#071827",
                  fontWeight: 900,
                  fontSize: 16,
                  alignSelf: "center",
                }}
              >
                ☰ Menu
              </button>
            )}

            <div
              style={{
                display: isMobilePublicNav && !publicMenuOpen ? "none" : "flex",
                alignItems: "center",
                justifyContent: isMobilePublicNav ? "center" : "flex-start",
                gap: isMobilePublicNav ? 8 : 10,
                flexWrap: "wrap",
                width: isMobilePublicNav ? "100%" : "auto",
                paddingTop: isMobilePublicNav ? 10 : 0,
              }}
            >

{isAuthed && me?.tenant_name && (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginRight: 16,
      flexWrap: "wrap",
    }}
  >
    <span
      style={{
        fontWeight: 700,
        fontSize: 16,
        color: "#00c2ff",
      }}
    >
      {me.tenant_name}
    </span>

    {me?.subscription_plan && (
      <span
        style={{
          padding: "2px 8px",
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 700,
          background: "#0f2235",
          border: "1px solid #27496b",
          color: "#d7e7f5",
        }}
      >
        {String(me.subscription_plan).charAt(0).toUpperCase() +
          String(me.subscription_plan).slice(1)} Plan
      </span>
    )}
  </div>
)}
            <NavLink to="/clients/management" style={linkStyle}>
              Clients
            </NavLink>

            <NavLink to="/clients-jobs" style={linkStyle}>
              Home
            </NavLink>

            <NavLink to="/estimates" style={linkStyle}>
              Estimates
            </NavLink>

            <NavLink to="/invoices" style={linkStyle}>
              Invoices
            </NavLink>

            <NavLink to="/products-services" style={linkStyle}>
              {isMobilePublicNav ? "Products" : "Products & Services"}
            </NavLink>

            <NavLink to="/reports" style={linkStyle}>
              Reports
            </NavLink>

            <NavLink to="/servers" style={linkStyle}>
              Employees
            </NavLink>

            <NavLink to="/profile" style={linkStyle}>
              Settings
            </NavLink>

            {!isAuthed && isRootHost && (
              <>
                <NavLink to="/signup" style={linkStyle}>
                  {isMobilePublicNav ? "Signup" : "Sign up"}
                </NavLink>

                <NavLink
                  to="/beta-signup"
                  style={{
                    ...linkStyle,
                    background: "#22c55e",
                    color: "#052e16",
                    borderColor: "rgba(34,197,94,0.55)",
                    fontWeight: 900,
                  }}
                >
                  {isMobilePublicNav ? "Beta" : "Join Beta"}
                </NavLink>
              </>
            )}

            </div>

            {isTenantHost && (
              <span
                title="Tenant"
                style={{
                  marginLeft: 6,
                  padding: "2px 8px",
                  border: "1px solid #27496b",
                  borderRadius: 999,
                  fontSize: 12,
                  color: "#ffffff",
                  background: "#0f2235",
                }}
              >
                {tenantSubdomain}
              </span>
            )}
          </div>

          {isAuthed && (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                title="Server time (Pacific)"
                style={{
                  fontSize: 12,
                  color: "#d7e7f5",
                  padding: "2px 8px",
                  border: "1px solid #27496b",
                  borderRadius: 999,
                  background: "#0f2235",
                  whiteSpace: "nowrap",
                }}
              >
                {serverTime?.server_time_pacific
                  ? `Server: ${new Date(serverTime.server_time_pacific).toLocaleString()}`
                  : "Server: …"}
              </div>

              <button
                onClick={handleLogout}
                title="Sign out"
                style={{
                  padding: "8px 14px",
                  background: "#00c2ff",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  color: "#062033",
                  fontWeight: 700,
                }}
              >
                Logout
              </button>
            </div>
          )}
        </header>}

        <div style={{ padding: usesAppShell ? 0 : 40 }}>
          <Routes>
           {/* Employees */}
            <Route
              path="/employees"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <AppShell
                    me={me}
                    serverTime={serverTime}
                    onLogout={handleLogout}
                  >
                    <Employees />
                  </AppShell>
                </ProtectedRoute>
              }
            />

           {/* Servers */}
            <Route
              path="/servers/:serverId/edit"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <ServerEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/servers"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <Servers />
                </ProtectedRoute>
              }
            />

 {/* Admin Platform */}
<Route
  path="/platform"
  element={
    <PlatformRoute me={me} authChecked={authChecked}>
      <AppShell
        me={me}
        serverTime={serverTime}
        onLogout={handleLogout}
      >
        <PlatformDashboard />
      </AppShell>
    </PlatformRoute>
  }
/>


<Route
  path="/platform/tenant-intelligence"
  element={
    <PlatformRoute me={me} authChecked={authChecked}>
      <AppShell
        me={me}
        serverTime={serverTime}
        onLogout={handleLogout}
      >
        <PlatformTenantIntelligence />
      </AppShell>
    </PlatformRoute>
  }
/>

<Route
  path="/platform/tenants"
  element={
    <PlatformRoute me={me} authChecked={authChecked}>
      <AppShell
        me={me}
        serverTime={serverTime}
        onLogout={handleLogout}
      >
        <PlatformTenants />
      </AppShell>
    </PlatformRoute>
  }
/>

<Route
  path="/platform/tenants/:tenantId"
  element={
    <PlatformRoute me={me} authChecked={authChecked}>
      <AppShell
        me={me}
        serverTime={serverTime}
        onLogout={handleLogout}
      >
        <PlatformTenantDetail />
      </AppShell>
    </PlatformRoute>
  }
/>


<Route
  path="/platform/release-center"
  element={
    <PlatformRoute me={me} authChecked={authChecked}>
      <AppShell me={me} serverTime={serverTime} onLogout={handleLogout}>
        <PlatformReleaseCenter />
      </AppShell>
    </PlatformRoute>
  }
/>

<Route
  path="/platform/stripe-connect"
  element={
    <PlatformRoute me={me} authChecked={authChecked}>
      <AppShell
        me={me}
        serverTime={serverTime}
        onLogout={handleLogout}
      >
        <PlatformStripeConnect />
      </AppShell>
    </PlatformRoute>
  }
/>


<Route
  path="/platform/stripe-billing"
  element={
    <PlatformRoute me={me} authChecked={authChecked}>
      <AppShell
        me={me}
        serverTime={serverTime}
        onLogout={handleLogout}
      >
        <PlatformStripeBilling />
      </AppShell>
    </PlatformRoute>
  }
/>

<Route
  path="/platform/billing"
  element={
    <PlatformRoute me={me} authChecked={authChecked}>
      <AppShell
        me={me}
        serverTime={serverTime}
        onLogout={handleLogout}
      >
        <PlatformBillingControl />
      </AppShell>
    </PlatformRoute>
  }
/>

            {/* Invoices */}
            <Route
              path="/invoices"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <AppShell
                    me={me}
                    serverTime={serverTime}
                    onLogout={handleLogout}
                  >
                    <Invoices />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/estimates"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <AppShell
                    me={me}
                    serverTime={serverTime}
                    onLogout={handleLogout}
                  >
                    <Estimates />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/estimates/new"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <AppShell
                    me={me}
                    serverTime={serverTime}
                    onLogout={handleLogout}
                  >
                    <EstimateNew />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/estimates/:invoiceId/print"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <EstimatePrint />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices/new"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <AppShell
                    me={me}
                    serverTime={serverTime}
                    onLogout={handleLogout}
                  >
                    <InvoiceNew />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices/:invoiceId"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <AppShell
                    me={me}
                    serverTime={serverTime}
                    onLogout={handleLogout}
                  >
                    <InvoiceDetail />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/estimates/:invoiceId"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <AppShell
                    me={me}
                    serverTime={serverTime}
                    onLogout={handleLogout}
                  >
                    <EstimateDetail />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices/:invoiceId/print"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <InvoicePrint />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices/:invoiceId/payments/:paymentId/receipt"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <InvoiceReceipt />
                </ProtectedRoute>
              }
            />

            {/* Billing */}
<Route
  path="/billing"
  element={
    <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
      <BillingPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/billing/success"
  element={
    <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
      <BillingSuccess />
    </ProtectedRoute>
  }
/>

<Route
  path="/billing/cancel"
  element={
    <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
      <BillingCancel />
    </ProtectedRoute>
  }
/>


<Route
  path="/billing/connect/success"
  element={
    <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
      <AppShell me={me} serverTime={serverTime} onLogout={handleLogout}>
        <InvoicePaymentsReturn />
      </AppShell>
    </ProtectedRoute>
  }
/>

<Route path="/billing-required" element={<BillingRequired />} />
<Route path="/suspended" element={<Suspended />} />

            {/* Judgments */}
            <Route
              path="/judgments"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <JudgmentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/judgments/:jid"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <JudgmentDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/judgments/new"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <CreateJudgmentWizard />
                </ProtectedRoute>
              }
            />

{/* Clients / Jobs */}
<Route
  path="/clients"
  element={<Navigate to="/clients-jobs" replace />}
/>


<Route
  path="/clients/new"
  element={
    <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
      <AppShell
        me={me}
        serverTime={serverTime}
        onLogout={handleLogout}
      >
        <ClientNew />
      </AppShell>
    </ProtectedRoute>
  }
/>

<Route
  path="/clients/:clientId/edit"
  element={
    <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
      <AppShell
        me={me}
        serverTime={serverTime}
        onLogout={handleLogout}
      >
        <ClientEdit />
      </AppShell>
    </ProtectedRoute>
  }
/>

<Route
  path="/clients/:clientId"
  element={
    <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
      <AppShell
        me={me}
        serverTime={serverTime}
        onLogout={handleLogout}
      >
        <ClientDetail />
      </AppShell>
    </ProtectedRoute>
  }
/>

<Route
  path="/clients/management"
  element={
    <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
      <AppShell
        me={me}
        serverTime={serverTime}
        onLogout={handleLogout}
      >
        <ClientManagement />
      </AppShell>
    </ProtectedRoute>
  }
/>

<Route
  path="/jobs/:jobId"
  element={
    <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
      <AppShell
        me={me}
        serverTime={serverTime}
        onLogout={handleLogout}
      >
        <JobDetail />
      </AppShell>
    </ProtectedRoute>
  }
/>

<Route
  path="/clients-jobs"
  element={
    <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
      <AppShell
        me={me}
        serverTime={serverTime}
        onLogout={handleLogout}
      >
        <ClientsJobs />
      </AppShell>
    </ProtectedRoute>
  }
/>

<Route
  path="/platform/beta-controls"
  element={
    <PlatformRoute isAuthed={isAuthed} authChecked={authChecked} me={me}>
      <AppShell
        me={me}
        serverTime={serverTime}
        onLogout={handleLogout}
      >
        <PlatformBetaControls />
      </AppShell>
    </PlatformRoute>
  }
/>

<Route
  path="/products-services"
  element={
    <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
      <AppShell
        me={me}
        serverTime={serverTime}
        onLogout={handleLogout}
      >
        <ProductsServices />
      </AppShell>
    </ProtectedRoute>
  }
/>
<Route
  path="/login"
  element={
    isAuthed ? (
      <Navigate to="/clients-jobs" replace />
    ) : (
      <Login />
    )
  }
/>
<Route
  path="/signup"
  element={
    isAuthed ? (
      <Navigate to="/clients-jobs" replace />
    ) : (
      <Signup />
    )
  }
/>

<Route
  path="/beta-signup"
  element={<Signup betaMode />}
/>

<Route
  path="/app/beta-signup"
  element={<Signup betaMode />}
/>


<Route
  path="/workspace-created"
  element={
    isRootHost ? (
      <WorkspaceCreated />
    ) : (
      <Navigate to="/login" replace />
    )
  }
/>

<Route
  path="/app/workspace-created"
  element={<WorkspaceCreated />}
/>

<Route path="/forgot-password" element={<ForgotPassword />} />
<Route path="/reset-password" element={<ResetPassword />} />

            <Route
              path="/integrations/thumbtack/leads"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <AppShell
                    me={me}
                    serverTime={serverTime}
                    onLogout={handleLogout}
                  >
                    <ThumbtackLeads />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrations/thumbtack"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <AppShell
                    me={me}
                    serverTime={serverTime}
                    onLogout={handleLogout}
                  >
                    <ThumbtackIntegration />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <AppShell
                    me={me}
                    serverTime={serverTime}
                    onLogout={handleLogout}
                  >
                    <BusinessProfile />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <AppShell
                    me={me}
                    serverTime={serverTime}
                    onLogout={handleLogout}
                  >
                    <Reports />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/books"
              element={
                <ProtectedRoute isAuthed={isAuthed} authChecked={authChecked}>
                  <AppShell
                    me={me}
                    serverTime={serverTime}
                    onLogout={handleLogout}
                  >
                    <Books />
                  </AppShell>
                </ProtectedRoute>
              }
            />

            {/* /app now redirects to Jobs because Jobs is the operational home screen */}
            <Route
              path="/app"
              element={<Navigate to="/clients-jobs" replace />}
            />

            {/* Home */}
            <Route
              path="/"
              element={<Navigate to="/app" replace />}
            />
          </Routes>
        </div>
      </div>
    </div>
  );
}
